import React, { useEffect, useState, useRef } from "react";
import Player from "@vimeo/player";

import InVideoTest from "./InVideoTest/index";
import Button from "~/components/form/Button";
import { Card, ErrorContent, Overlay } from "./InVideoTest/style";

import { FaExclamationTriangle } from "react-icons/fa";

// style
import {
  VideoFrame,
  WarningCard,
  WarningTitle,
  WarningDescription,
  WarningInfoBox,
  WarningActions,
} from "./style";

const Video = ({
  idVideo,
  setPercentagePlayed,
  historicClass,
  videoTests = [],
  lessonUpdatedWarning,
  setLessonUpdatedWarning,
}) => {
  const [showOverlay, setShowOverlay] = useState(true);
  const iframe_video = useRef();
  const [player, setPlayer] = useState(null);
  const [percentagePlayedControl, setPercentagePlayedControl] = useState(
    historicClass.video_assistido_porcentagem,
  );

  const [activeTest, setActiveTest] = useState(null);
  const [completedTestIds, setCompletedTestIds] = useState([]);

  // refs para o Vimeo ler os dados atualizados sem bugs
  const videoTestsRef = useRef(videoTests);
  const completedTestsRef = useRef(completedTestIds);
  const activeTestRef = useRef(null);
  const timeToSeekRef = useRef(null);
  const latestTimeRef = useRef(0);
  const initialResumeDoneRef = useRef(false);
  const initialPercentageRef = useRef(
    historicClass.video_assistido_porcentagem || 0,
  );

  useEffect(() => {
    const sortedTests = [...videoTests].sort((a, b) => {
      const timeA = Number(a.tempo_video);
      const timeB = Number(b.tempo_video);
      return timeA === timeB ? a.idtestes - b.idtestes : timeA - timeB;
    });

    videoTestsRef.current = sortedTests;

    if (sortedTests.length > 0) {
      // filtra testes que JÁ ESTÃO na tabela teste_resultado
      const testsAlreadyDone = sortedTests
        .filter((t) => {
          if (!t.teste_resultado || t.teste_resultado.length === 0)
            return false;

          // verifica aprovação
          const aprovado = t.teste_resultado.some(
            (r) => r.aprovado === 1 || r.aprovado === true,
          );

          return aprovado;
        })
        .map((t) => t.idtestes);

      if (testsAlreadyDone.length > 0) {
        setCompletedTestIds((prev) => [
          ...new Set([...prev, ...testsAlreadyDone]),
        ]);
      } else {
        console.warn("Nenhum teste identificadoo.");
      }
    }
  }, [videoTests]);

  useEffect(() => {
    if (lessonUpdatedWarning) {
      timeToSeekRef.current = latestTimeRef.current;
    }
  }, [lessonUpdatedWarning]);

  useEffect(() => {
    completedTestsRef.current = completedTestIds;
  }, [completedTestIds]);

  // sincronizar a ref do teste ativo
  useEffect(() => {
    activeTestRef.current = activeTest;
  }, [activeTest]);

  // cria instância do player dinamicamente
  useEffect(() => {
    // se não tem ref do iframe OU se tem um teste na tela, aborta.
    if (!iframe_video.current || activeTest || lessonUpdatedWarning) return;

    // toda vez que o player é recriado (ex.: depois de um teste), o baseline de
    // segundos já assistidos precisa refletir o progresso real até agora, não o
    // valor congelado de quando a página carregou.
    initialPercentageRef.current = percentagePlayedControl;

    const newPlayer = new Player(iframe_video.current);
    setPlayer(newPlayer);

    newPlayer.ready().then(() => {
      if (timeToSeekRef.current !== null) {
        const timeToStart = timeToSeekRef.current;
        timeToSeekRef.current = null;

        console.log(`⏳ Iniciando no segundo pós-teste: ${timeToStart}`);

        setTimeout(() => {
          if (timeToStart === 0) {
            newPlayer
              .play()
              .catch((e) => console.log("Erro auto-play no 0", e));
          } else {
            newPlayer.setCurrentTime(timeToStart).then(() => {
              newPlayer
                .play()
                .catch((e) => console.log("Erro auto-play no seek", e));
            });
          }
        }, 200);

        // se for o primeiro carregamento da página, retoma da % salva
      } else if (!initialResumeDoneRef.current) {
        initialResumeDoneRef.current = true; // Marca que o pulo inicial já foi feito!
        const savedPercentage = historicClass.video_assistido_porcentagem || 0;

        // só pula se tiver algum progresso. E se já for 100%, deixam no 0 pra ele rever.
        if (savedPercentage > 0 && savedPercentage < 100) {
          newPlayer.getDuration().then((duration) => {
            // calculo (Porcentagem / 100) * duracao total
            const secondsToSeek = (savedPercentage / 100) * duration;

            newPlayer
              .setCurrentTime(secondsToSeek)
              .then(() => {
                // se quiser que o vídeo dê play sozinho ao recarregar a página, tire o comentário da linha abaixo:
                // newPlayer.play();
              })
              .catch((e) => console.log("Erro ao retomar progresso:", e));
          });
        }
      }
    });

    return () => {
      newPlayer.destroy().catch((e) => console.log("Erro ao destruir", e));
      setPlayer(null);
    };
  }, [activeTest, lessonUpdatedWarning]);

  useEffect(() => {
    if (!player) return;

    let intervalSeconds = 2;
    let controlIntervalSeconds = intervalSeconds;
    let lastSecond = 0;

    const handlerGetTimeIntervalPlayed = (data) => {
      if (activeTestRef.current) return;
      latestTimeRef.current = data.seconds;

      const currentSeconds = data.seconds;
      const tests = videoTestsRef.current;
      const completed = completedTestsRef.current;
      const currentTimeFloor = Math.floor(currentSeconds);

      const testToTrigger = tests.find((t) => {
        const timeDb = Number(t.tempo_video);
        return timeDb === currentTimeFloor && !completed.includes(t.idtestes);
      });

      if (testToTrigger) {
        player.pause().then(() => {
          setActiveTest((prev) =>
            prev?.idtestes === testToTrigger.idtestes ? prev : testToTrigger,
          );
        });
      }

      const videoDuration = data.duration;

      if (
        currentSeconds >= controlIntervalSeconds ||
        currentSeconds < lastSecond
      ) {
        lastSecond = currentSeconds;
        controlIntervalSeconds = currentSeconds + intervalSeconds;

        player
          .getPlayed()
          .then((played) => {
            const savedPercentage = initialPercentageRef.current;
            // transforma a % do banco em segundos reais (Ex: 50% de 100s = 50s)
            const baseSeconds = (savedPercentage / 100) * videoDuration;

            let newPlayedSeconds = 0;

            played.forEach((itemInterval) => {
              const start = itemInterval[0];
              const end = itemInterval[1];

              // só soma os segundos NOVOS que ultrapassam a marca do que já foi assistido antes
              if (end > baseSeconds) {
                const actualStart = Math.max(start, baseSeconds);
                newPlayedSeconds += end - actualStart;
              }
            });

            // segundos antigos do banco + segundos novos da sessão atual
            const totalEffectiveSeconds = baseSeconds + newPlayedSeconds;
            const percentagePlayed = Math.round(
              (totalEffectiveSeconds * 100) / videoDuration,
            );

            if (percentagePlayed > percentagePlayedControl) {
              setPercentagePlayedControl(percentagePlayed);
              setPercentagePlayed(percentagePlayed);
            }
          })
          .catch((err) => console.log("countTimePlayed error", err));
      }
    };

    const handlerEndedVideo = (data) => {
      const videoDuration = data.duration;

      player
        .getPlayed()
        .then((played) => {
          const savedPercentage = initialPercentageRef.current;
          const baseSeconds = (savedPercentage / 100) * videoDuration;

          let newPlayedSeconds = 0;
          played.forEach((itemInterval) => {
            const start = itemInterval[0];
            const end = itemInterval[1];
            if (end > baseSeconds) {
              const actualStart = Math.max(start, baseSeconds);
              newPlayedSeconds += end - actualStart;
            }
          });

          const totalEffectiveSeconds = baseSeconds + newPlayedSeconds;

          // se a soma (velhos + novos) chegar até o final do vídeo (com 1 seg de margem)
          if (totalEffectiveSeconds >= videoDuration - 1) {
            setPercentagePlayedControl(100);
            setPercentagePlayed(100);
          }
        })
        .catch(console.log);
    };
    const handlerPreventPlay = () => {
      if (activeTestRef.current) {
        player.pause();
      }
    };

    const handlerSeeked = (data) => {
      if (activeTestRef.current) return;

      const newTime = data.seconds;
      const tests = videoTestsRef.current;
      const completed = completedTestsRef.current;

      const skippedTest = tests.find(
        (t) =>
          Number(t.tempo_video) < newTime && !completed.includes(t.idtestes),
      );

      if (skippedTest) {
        player.pause().then(() => {
          player.setCurrentTime(Number(skippedTest.tempo_video)).then(() => {
            player.play();
          });
        });
      }
    };

    player.on("timeupdate", handlerGetTimeIntervalPlayed);
    player.on("ended", handlerEndedVideo);
    player.on("play", handlerPreventPlay);
    player.on("seeked", handlerSeeked);

    return () => {
      // Limpa tudo quando desmontar
      player.off("timeupdate", handlerGetTimeIntervalPlayed);
      player.off("ended", handlerEndedVideo);
      player.off("play", handlerPreventPlay);
      player.off("seeked", handlerSeeked);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, percentagePlayedControl, setPercentagePlayed]);

  // recebe o teste que acabou de ser feito como argumento para evitar closure stale
  const handleResumeVideo = (aprovado, testJustFinished) => {
    const finishedTest =
      testJustFinished || activeTest || activeTestRef.current;

    if (!finishedTest) {
      activeTestRef.current = null;
      setActiveTest(null);
      player.play();
      return;
    }

    if (aprovado) {
      const newCompletedList = [...completedTestIds, finishedTest.idtestes];
      setCompletedTestIds(newCompletedList);
      completedTestsRef.current = newCompletedList;

      const currentTestTime = Number(finishedTest.tempo_video);
      const tests = videoTestsRef.current;
      const nextTestAtSameTime = tests.find(
        (t) =>
          Number(t.tempo_video) === currentTestTime &&
          !newCompletedList.includes(t.idtestes),
      );

      if (nextTestAtSameTime) {
        activeTestRef.current = nextTestAtSameTime;
        setActiveTest(nextTestAtSameTime);
      } else {
        timeToSeekRef.current = Number(finishedTest.tempo_video);

        activeTestRef.current = null;
        setActiveTest(null);
      }
    } else {
      // lógica rever o video
      activeTestRef.current = null; // libera bloqueio
      setActiveTest(null);

      const tests = videoTestsRef.current;
      // acha a posição deste teste na fila
      const currentIndex = tests.findIndex(
        (t) => t.idtestes === finishedTest.idtestes,
      );

      let timeToSeek = 0;

      if (currentIndex > 0) {
        // se NÃO for o primeiro teste, tenta voltar para o teste anterior
        const prevTest = tests[currentIndex - 1];

        const currentTime = Number(finishedTest.tempo_video);
        const prevTime = Number(prevTest.tempo_video);

        // garanti que volta para o teste anterior que não está encadeado
        if (prevTime === currentTime) {
          let safeIndex = currentIndex - 1;
          while (
            safeIndex >= 0 &&
            Number(tests[safeIndex].tempo_video) === currentTime
          ) {
            safeIndex--;
          }

          if (safeIndex >= 0) {
            // achou um teste anterior com tempo diferente
            timeToSeek = Number(tests[safeIndex].tempo_video);
          } else {
            // se voltou tudo e todos eram no mesmo segundo, vai pro 0
            timeToSeek = 0;
          }
        } else {
          // volta pro tempo do teste anterior
          timeToSeek = prevTime;
        }
      }

      timeToSeekRef.current = timeToSeek;
      activeTestRef.current = null;
      setActiveTest(null);
    }
  };

  let hasNextTestAtSameTime = false;

  if (activeTest && videoTestsRef.current) {
    const sortedTests = videoTestsRef.current;
    // acha o índice do teste atual na lista ordenada
    const currentIndex = sortedTests.findIndex(
      (t) => t.idtestes === activeTest.idtestes,
    );
    const nextTest = sortedTests[currentIndex + 1];

    // se existir próximo E o tempo for igual ao atual é um encadeamento
    if (
      nextTest &&
      Number(nextTest.tempo_video) === Number(activeTest.tempo_video)
    ) {
      hasNextTestAtSameTime = true;
    }
  }

  const video_url = `https://player.vimeo.com/video/${idVideo.trim()}`;

  const iniciarVideoReact = () => {
    const iframe = iframe_video.current;
    if (!iframe) return;

    try {
      if (player && iframe.src.includes("vimeo.com")) {
        player.play().catch((e) => console.log("Erro ao dar play (overlay)", e));
      } else if (iframe.src.includes("youtube.com")) {
        iframe.contentWindow.postMessage(
          '{"event":"command","func":"playVideo","args":""}',
          "*",
        );
      } else {
        const videoNativo = iframe.contentDocument?.querySelector("video");
        if (videoNativo) videoNativo.play();
      }
    } catch (err) {
      console.log("Comando enviado via React reference.", err);
    }

    iframe.removeAttribute("tabindex");
    iframe.removeAttribute("aria-hidden");

    setShowOverlay(false);
    iframe.focus();
  };

  return (
    <div
      className="ba-video-container"
      onMouseEnter={() => {
        // depois que o overlay some (vídeo já em reprodução), ele deixa de existir e,
        // com ele, o único elemento que falava algo sobre o vídeo. Sem isso aqui, o
        // vídeo fica mudo pro resto da sessão até recarregar a página.
        if (showOverlay || activeTest || lessonUpdatedWarning || !player) return;
        const pref = localStorage.getItem("user_accessibility");
        if (pref === "1") {
          player.getPaused().then((paused) => {
            window.speechSynthesis.cancel();
            const falar = new SpeechSynthesisUtterance(
              paused ? "Vídeo da aula pausado." : "Vídeo da aula em reprodução.",
            );
            falar.lang = "pt-BR";
            falar.rate = 1.2;
            window.speechSynthesis.speak(falar);
          });
        }
      }}
    >
      {/* overlay de acessibilidade: só aparece antes do primeiro play, e nunca sobre um teste ou aviso */}
      {showOverlay && !activeTest && !lessonUpdatedWarning && (
        <div
          className="ba-player-overlay"
          tabIndex="0"
          role="button"
          aria-label="Vídeo da aula. Pressione Enter ou Espaço para iniciar a reprodução."
          onFocus={() => {
            const pref = localStorage.getItem("user_accessibility");
            if (pref === "1") {
              window.speechSynthesis.cancel();
              const falar = new SpeechSynthesisUtterance(
                "Vídeo da aula. Pressione Enter ou Espaço para iniciar a reprodução.",
              );
              falar.lang = "pt-BR";
              falar.rate = 1.2;
              window.speechSynthesis.speak(falar);
            }
          }}
          onMouseEnter={() => {
            const pref = localStorage.getItem("user_accessibility");
            if (pref === "1") {
              window.speechSynthesis.cancel();
              const falar = new SpeechSynthesisUtterance(
                "Vídeo da aula. Pressione Enter ou Espaço para iniciar a reprodução.",
              );
              falar.lang = "pt-BR";
              falar.rate = 1.2;
              window.speechSynthesis.speak(falar);
            }
          }}
          onClick={() => iniciarVideoReact()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              iniciarVideoReact();
            }
          }}
        />
      )}

      <VideoFrame
        style={{ position: "relative" }}
        isTesting={!!activeTest || lessonUpdatedWarning}
      >
        {/* o iframe SÓ aparece se NÃO houver teste e NÃO houver aviso na tela */}
        {!activeTest && !lessonUpdatedWarning && (
          <iframe
            src={video_url}
            allow="autoplay; fullscreen"
            title="Vídeo aula"
            ref={iframe_video}
            tabIndex={showOverlay ? "-1" : undefined}
            aria-hidden={showOverlay ? "true" : undefined}
            style={{ width: "100%", height: "100%", border: "none" }}
          ></iframe>
        )}

        {/* o teste aparece se houver teste e não houver aviso global */}
        {activeTest && !lessonUpdatedWarning && (
          <InVideoTest
            key={activeTest.idtestes}
            currentTest={activeTest}
            idHistoricClass={historicClass.idhistorico_aulas}
            onResume={handleResumeVideo}
            hasNextTest={hasNextTestAtSameTime}
            setLessonUpdatedWarning={setLessonUpdatedWarning}
          />
        )}

        {/* a mensagem assume o lugar do vídeo usando o Overlay! */}
        {lessonUpdatedWarning && (
          <Overlay>
            <WarningCard>
              <FaExclamationTriangle
                size={50}
                color="#f39c12"
                style={{ marginBottom: 20 }}
              />
              <WarningTitle>Atenção: Aula Atualizada</WarningTitle>
              <WarningDescription>
                Percebemos que esta aula recebeu uma atualização enquanto você
                assistia.
              </WarningDescription>

              <WarningInfoBox>
                <strong>
                  Para garantir que você esteja vendo a versão mais recente,
                  por favor, clique no botão abaixo para atualizar a página.
                </strong>
              </WarningInfoBox>

              <WarningActions>
                <Button
                  width="250px"
                  theme={{ bdc_hover: "#878787" }}
                  onClick={() => window.location.reload()}
                >
                  Atualizar Agora
                </Button>
              </WarningActions>
            </WarningCard>
          </Overlay>
        )}
      </VideoFrame>
    </div>
  );
};
export default Video;
